import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useBrandLogos } from '../lib/useBrandLogos';
import {
  Building2, Dumbbell, Coffee, ShoppingBag, ArrowUpRight, Sparkles, Lock, Mail, MapPin, Phone
} from 'lucide-react';

const brands = [
  {
    key: 'svt-co',
    name: 'SVT & Co',
    tagline: 'Business Strategy, Consulting & Development Consultants',
    description: 'Tax filings, GST, audits, and compliance for 200+ clients across Bengaluru since 2004.',
    accent: '#1B2A1B',
    accentSoft: '#E6EDE6',
    icon: Building2,
    href: '/svt-co',
    type: 'internal',
    status: 'live',
    cta: 'Visit Website',
  },
  {
    key: 'fitness',
    name: 'SVT Golden Fitness',
    tagline: 'Premium fitness studio',
    description: 'Strength, conditioning, and wellness programs in a state-of-the-art facility.',
    accent: '#B8860B',
    accentSoft: '#FBF5E6',
    icon: Dumbbell,
    href: 'https://www.svtgoldenfitness.com',
    type: 'external',
    status: 'live',
    cta: 'Open svtgoldenfitness.com',
  },
  {
    key: 'cafe',
    name: 'Swiss Cafe',
    tagline: 'Café & Patisserie',
    description: 'European-inspired café concept — coming soon to Whitefield.',
    accent: '#8B6F47',
    accentSoft: '#F5EFE7',
    icon: Coffee,
    href: '#',
    type: 'soon',
    status: 'soon',
    cta: 'Coming Soon',
  },
  {
    key: 'vrock',
    name: 'SVT V Rock',
    tagline: 'Sales Platform',
    description: 'Multi-vendor sales platform — launching next on this domain.',
    accent: '#2E4F2E',
    accentSoft: '#E8F0E8',
    icon: ShoppingBag,
    href: '#',
    type: 'soon',
    status: 'soon',
    cta: 'Launching Soon',
  },
];

export default function GroupHomePage() {
  const [tagline, setTagline] = useState('A diversified group serving Bengaluru across taxation, fitness, hospitality, and retail.');
  const { info, groupLogo } = useBrandLogos();

  useEffect(() => {
    if (info.tagline) setTagline(info.tagline);
  }, [info.tagline]);

  return (
    <div className="min-h-screen bg-[#FBFAF6] font-body" data-testid="group-home-page">
      {/* Top Bar */}
      <div className="bg-[#0F1A0F] text-amber-100/60 text-xs py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <span className="tracking-wider uppercase text-[10px]">SVT Aaventures · Group of Companies</span>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><MapPin size={11} /> Whitefield, Bengaluru</span>
            <Link to="/login" className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors" data-testid="group-admin-link">
              <Lock size={11} /> Admin
            </Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/90 backdrop-blur-md border-b border-[#E8E0CC]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {groupLogo ? (
              <img src={groupLogo} alt="SVT Aaventures" className="w-11 h-11 object-contain" data-testid="group-header-logo" />
            ) : (
              <div className="w-11 h-11 rounded-sm bg-[#0F1A0F] flex items-center justify-center" data-testid="group-header-logo-default">
                <span className="font-heading text-lg font-bold text-[#D4AF37]">SA</span>
              </div>
            )}
            <div>
              <p className="font-heading text-lg font-semibold text-[#0F1A0F] leading-tight">SVT Aaventures</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B6F47] -mt-0.5">Group of Companies</p>
            </div>
          </div>
          <Link
            to="/login"
            className="md:hidden inline-flex items-center gap-1.5 text-xs text-[#0F1A0F] border border-[#0F1A0F]/20 rounded-sm px-3 py-1.5"
            data-testid="mobile-admin-link"
          >
            <Lock size={12} /> Admin
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FBFAF6] via-[#F5EFE0] to-[#FBFAF6]" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#0F1A0F]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-6">
            <Sparkles size={12} className="text-[#B8860B]" />
            <span className="text-[10px] text-[#8B6F47] font-semibold tracking-[0.15em] uppercase">A Multi-Brand Enterprise</span>
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#0F1A0F] tracking-tight leading-[1.05] max-w-3xl">
            One vision.<br />
            <span className="text-[#B8860B]">Four distinct</span> brands.
          </h1>
          <p className="mt-6 text-base text-[#3D362A] leading-relaxed max-w-xl">
            {tagline}
          </p>
        </div>
      </section>

      {/* Brand Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-24" id="brands">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {brands.map((b) => {
            const isSoon = b.status === 'soon';
            const Wrapper = isSoon ? 'div' : b.type === 'external' ? 'a' : Link;
            const wrapperProps = isSoon
              ? { 'aria-disabled': 'true' }
              : b.type === 'external'
                ? { href: b.href, target: '_blank', rel: 'noreferrer noopener' }
                : { to: b.href };
            return (
              <Wrapper
                key={b.key}
                {...wrapperProps}
                data-testid={`brand-card-${b.key}`}
                className={`group relative overflow-hidden rounded-sm border bg-white transition-all duration-300 ${
                  isSoon
                    ? 'border-[#E8E0CC] opacity-80 cursor-default'
                    : 'border-[#E8E0CC] hover:shadow-xl hover:-translate-y-1 cursor-pointer'
                }`}
                style={{ borderTop: `3px solid ${b.accent}` }}
              >
                {/* Soft accent fill */}
                <div className="absolute top-0 right-0 w-40 h-40 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" style={{ background: b.accentSoft }} />

                <div className="relative p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-sm flex items-center justify-center" style={{ background: b.accent }}>
                      <b.icon size={26} className="text-white" strokeWidth={1.5} />
                    </div>
                    {isSoon ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8B6F47] bg-[#F5EFE0] border border-[#E8E0CC] rounded-full px-3 py-1">
                        Coming Soon
                      </span>
                    ) : b.type === 'external' ? (
                      <ArrowUpRight size={20} className="text-[#8B6F47] group-hover:text-[#0F1A0F] transition-colors" />
                    ) : (
                      <ArrowUpRight size={20} className="text-[#8B6F47] group-hover:text-[#0F1A0F] transition-colors" />
                    )}
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#0F1A0F] mb-1">{b.name}</h3>
                  <p className="text-xs tracking-[0.12em] uppercase font-semibold mb-3" style={{ color: b.accent }}>{b.tagline}</p>
                  <p className="text-sm text-[#5C5447] leading-relaxed mb-6 min-h-[42px]">{b.description}</p>
                  <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: isSoon ? '#8B6F47' : b.accent }}>
                    <span>{b.cta}</span>
                    {!isSoon && <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />}
                  </div>
                  {b.key === 'svt-co' && (
                    <Link
                      to="/login"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute bottom-3 right-4 text-[11px] text-[#8B6F47] hover:text-[#0F1A0F] underline-offset-2 hover:underline inline-flex items-center gap-1"
                      data-testid="svt-co-admin-link"
                    >
                      <Lock size={10} /> Admin
                    </Link>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A0F] text-amber-50/70">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              {groupLogo ? (
                <img src={groupLogo} alt="SVT Aaventures" className="w-10 h-10 object-contain" data-testid="group-footer-logo" />
              ) : (
                <div className="w-10 h-10 rounded-sm bg-[#D4AF37] flex items-center justify-center" data-testid="group-footer-logo-default">
                  <span className="font-heading text-base font-bold text-[#0F1A0F]">SA</span>
                </div>
              )}
              <div>
                <p className="font-heading text-lg font-semibold text-[#D4AF37]">SVT Aaventures</p>
                <p className="text-[9px] tracking-[0.2em] uppercase text-amber-100/40">Group of Companies</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-amber-50/50">
              A diversified business group headquartered in Bengaluru.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-xs uppercase tracking-[0.15em] font-semibold text-[#D4AF37] mb-3">Brands</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/svt-co" className="hover:text-white">SVT & Co</Link></li>
              <li><a href="https://www.svtgoldenfitness.com" target="_blank" rel="noreferrer noopener" className="hover:text-white">SVT Golden Fitness</a></li>
              <li className="text-amber-100/30">Swiss Cafe (soon)</li>
              <li className="text-amber-100/30">SVT V Rock (soon)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-xs uppercase tracking-[0.15em] font-semibold text-[#D4AF37] mb-3">Reach Us</h4>
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2"><MapPin size={14} className="text-[#D4AF37] mt-0.5" /> Whitefield, Bengaluru</p>
              <p className="flex items-center gap-2"><Mail size={14} className="text-[#D4AF37]" /> info@svtaaventures.com</p>
              <p className="flex items-center gap-2"><Phone size={14} className="text-[#D4AF37]" /> +91 98765 43210</p>
            </div>
          </div>
        </div>
        <div className="border-t border-amber-100/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-amber-100/30">
            <span>&copy; {new Date().getFullYear()} SVT Aaventures. All rights reserved.</span>
            <Link to="/login" className="hover:text-[#D4AF37] inline-flex items-center gap-1" data-testid="footer-admin-link">
              <Lock size={10} /> Admin Portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
